/**
 * 지점 목록이 많아졌을 때(파트너 247지점 / 상품당 최대 122지점·60도시) 쓰는
 * 공통 정리 유틸 — 검색 필터, 지역 그룹핑, 거리순 정렬.
 *
 * 상품 상세 지점 선택기 / 지도 모달 / 파트너 지점 탭이 모두 같은 규칙을 쓰도록
 * 여기에 모아 둔다.
 */

import { calculateDistance } from "@/lib/api/products/geo";

/** 이 유틸이 요구하는 최소 지점 모양. Branch / PartnerBranch 모두 만족한다. */
export interface GroupableBranch {
  id: string;
  name: string;
  /** 시/도 레벨. 없으면 `city` 로 폴백한다. */
  state_province?: string | null;
  /** 상품 상세의 Branch 는 이미 state_province→city 폴백이 적용된 값이 들어온다. */
  city?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface BranchGroup<T extends GroupableBranch> {
  /** 그룹 식별자 — 라벨과 동일(소문자 정규화 전 최초 철자). */
  key: string;
  label: string;
  branches: T[];
}

/**
 * NFKC 정규화 + 소문자. 전각/반각·합성 문자가 섞인 일본어 지점명 검색을 위해
 * 필요하다("ﾄｳｷｮｳ" ↔ "トウキョウ").
 */
function normalize(value: string): string {
  return value.normalize("NFKC").toLowerCase();
}

/** 그룹 라벨(시/도). 값이 없으면 빈 문자열 — 호출부가 "기타"로 묶는다. */
export function getBranchRegion(branch: GroupableBranch): string {
  return (branch.state_province || branch.city || "").trim();
}

/**
 * 지점명 / 지역 / 주소를 대상으로 하는 토큰 AND 검색.
 * 공백으로 나눈 모든 토큰이 포함돼야 통과한다.
 */
export function filterBranchesByQuery<T extends GroupableBranch>(
  branches: T[],
  query: string,
): T[] {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return branches;
  return branches.filter((branch) => {
    const haystack = normalize(
      [branch.name, getBranchRegion(branch), branch.address ?? ""].join(" "),
    );
    return tokens.every((token) => haystack.includes(token));
  });
}

/**
 * 시/도(없으면 도시) 기준 그룹핑.
 *
 * 그룹 순서는 지점 수 내림차순 → 라벨 오름차순. 60개 도시에 걸친 상품에서
 * 사용자가 실제로 고를 확률이 높은 큰 지역이 위로 온다.
 * 지역 값이 비어 있는 지점은 `fallbackLabel` 그룹으로 모으고 항상 마지막.
 */
export function groupBranchesByRegion<T extends GroupableBranch>(
  branches: T[],
  fallbackLabel: string,
): BranchGroup<T>[] {
  const groups = new Map<string, BranchGroup<T>>();

  for (const branch of branches) {
    const region = getBranchRegion(branch);
    const key = region ? normalize(region) : "￿__other__";
    const existing = groups.get(key);
    if (existing) {
      existing.branches.push(branch);
    } else {
      groups.set(key, {
        key,
        label: region || fallbackLabel,
        branches: [branch],
      });
    }
  }

  return [...groups.values()].sort((a, b) => {
    const aOther = a.key === "￿__other__";
    const bOther = b.key === "￿__other__";
    if (aOther !== bOther) return aOther ? 1 : -1;
    if (a.branches.length !== b.branches.length)
      return b.branches.length - a.branches.length;
    return a.label.localeCompare(b.label);
  });
}

/**
 * 기준 좌표에서 가까운 순으로 정렬한 사본을 돌려준다.
 * 좌표가 없는 지점은 항상 뒤로 밀린다.
 */
export function sortBranchesByDistance<T extends GroupableBranch>(
  branches: T[],
  origin: { latitude: number; longitude: number },
): T[] {
  return [...branches]
    .map((branch) => ({
      branch,
      distance:
        branch.latitude != null && branch.longitude != null
          ? calculateDistance(
              origin.latitude,
              origin.longitude,
              branch.latitude,
              branch.longitude,
            )
          : Number.POSITIVE_INFINITY,
    }))
    .sort((a, b) => a.distance - b.distance)
    .map((entry) => entry.branch);
}
